import type {
	DashboardAgentCoveragePoint,
	DashboardAgentSummary,
	DashboardSummary,
	DashboardTrendPoint,
	MembershipSummary,
} from '@livechat/shared';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { io } from 'socket.io-client';

import {
	currentAccessToken,
	getDashboard,
	onAccessTokenChanged,
	realtimeUrl,
	refreshSession,
} from '../api.js';
import { teamMemberRoleLabel } from '../member-label.js';
import { useDateFormat } from '../date-format.js';
import { createRefreshRunner } from '../refresh-runner.js';
import { DateRangePicker, recentDateRange } from './DateRangePicker.js';
import { MemberAvatar } from './MemberAvatar.js';
import { WorldVisitorMap } from './WorldVisitorMap.js';

type DashboardDestination = 'chats' | 'history' | 'team' | 'visitors';

interface DashboardMetric {
	action?: DashboardDestination;
	kind: string;
	label: string;
	tone: string;
	value: string;
}

function MetricIcon({ kind }: { kind: string }) {
	const paths: Record<string, ReactNode> = {
		chat: (
			<>
				<path d="M5 6h14v10H9l-4 3z" />
				<path d="M9 10h6" />
			</>
		),
		clock: (
			<>
				<circle cx="12" cy="13" r="7" />
				<path d="M12 9v4l3 2M9 3h6" />
			</>
		),
		queue: (
			<>
				<path d="M5 7h14M5 12h10M5 17h7" />
				<path d="m17 15 2 2-2 2" />
			</>
		),
		total: (
			<>
				<path d="M7 4h10v16H7z" />
				<path d="M10 8h4M10 12h4M10 16h4" />
			</>
		),
		unanswered: (
			<>
				<path d="M5 6h14v10H9l-4 3z" />
				<path d="m8 8 8 6M16 8l-8 6" />
			</>
		),
		visitors: (
			<>
				<circle cx="9" cy="9" r="3" />
				<circle cx="16.5" cy="10" r="2.5" />
				<path d="M3.5 19c.6-3.2 2.4-5 5.5-5s5 1.8 5.5 5M14 15c3.6-.5 5.7.8 6.5 4" />
			</>
		),
	};
	return (
		<svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
			{paths[kind]}
		</svg>
	);
}

function formatDuration(seconds?: number): string {
	if (seconds === undefined) return '—';
	if (seconds < 60) return `${seconds}s`;
	const minutes = Math.floor(seconds / 60);
	const remainder = seconds % 60;
	return remainder ? `${minutes}m ${remainder}s` : `${minutes}m`;
}

function axisMaximum(points: DashboardTrendPoint[], key: 'chats' | 'visitors'): number {
	const maximum = Math.max(0, ...points.map((point) => point[key]));
	if (maximum <= 5) return 5;
	const magnitude = 10 ** Math.floor(Math.log10(maximum));
	return Math.ceil(maximum / magnitude) * magnitude;
}

function hitAreaBounds(index: number, pointCount: number, x: (index: number) => number) {
	if (pointCount === 1) return { left: x(0) - 20, right: x(0) + 20 };
	return {
		left: index === 0 ? x(index) : (x(index - 1) + x(index)) / 2,
		right: index === pointCount - 1 ? x(index) : (x(index) + x(index + 1)) / 2,
	};
}

function tooltipX(pointX: number, width: number, left: number, right: number): number {
	return Math.min(Math.max(pointX - width / 2, left), right - width);
}

export function TrendChart({ points }: { points: DashboardTrendPoint[] }) {
	const { formatDate } = useDateFormat();
	const [activeIndex, setActiveIndex] = useState<number>();
	const width = 760;
	const height = 270;
	const padding = { bottom: 35, left: 46, right: 52, top: 20 };
	const plotWidth = width - padding.left - padding.right;
	const plotHeight = height - padding.top - padding.bottom;
	const chatsMaximum = axisMaximum(points, 'chats');
	const visitorsMaximum = axisMaximum(points, 'visitors');
	const x = (index: number) =>
		padding.left +
		(points.length <= 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth);
	const y = (value: number, maximum: number) =>
		padding.top + plotHeight - (value / maximum) * plotHeight;
	const line = (key: 'chats' | 'visitors', maximum: number) =>
		points.map((point, index) => `${x(index)},${y(point[key], maximum)}`).join(' ');
	const labelInterval = Math.max(1, Math.ceil(points.length / 6));
	const activePoint = activeIndex === undefined ? undefined : points[activeIndex];
	const activeX = activeIndex === undefined ? undefined : x(activeIndex);
	const tooltipWidth = 188;

	if (!points.length) return <div className="dashboard-empty-chart">No activity yet.</div>;
	return (
		<div className="dashboard-chart-wrap">
			<svg
				aria-label="Daily chats and unique visitors"
				className="dashboard-chart"
				onMouseLeave={() => setActiveIndex(undefined)}
				role="group"
				viewBox={`0 0 ${width} ${height}`}
			>
				{[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
					const lineY = padding.top + plotHeight - ratio * plotHeight;
					return (
						<g key={ratio}>
							<line
								x1={padding.left}
								x2={width - padding.right}
								y1={lineY}
								y2={lineY}
							/>
							<text className="chart-axis-chat" x={padding.left - 9} y={lineY + 4}>
								{Math.round(chatsMaximum * ratio)}
							</text>
							<text
								className="chart-axis-visitor"
								x={width - padding.right + 9}
								y={lineY + 4}
							>
								{Math.round(visitorsMaximum * ratio)}
							</text>
						</g>
					);
				})}
				<polyline
					className="chart-line chart-line-visitors"
					pathLength="1"
					points={line('visitors', visitorsMaximum)}
				/>
				<polyline
					className="chart-line chart-line-chats"
					pathLength="1"
					points={line('chats', chatsMaximum)}
				/>
				{points.map((point, index) => (
					<g key={point.date}>
						{(index % labelInterval === 0 || index === points.length - 1) && (
							<text className="chart-date" x={x(index)} y={height - 8}>
								{formatDate(`${point.date}T12:00:00`)}
							</text>
						)}
						<rect
							aria-label={`${formatDate(`${point.date}T12:00:00`)}: ${point.chats} chats, ${point.visitors} visitors`}
							className="chart-hit"
							height={plotHeight}
							onBlur={() => setActiveIndex(undefined)}
							onFocus={() => setActiveIndex(index)}
							onMouseEnter={() => setActiveIndex(index)}
							role="img"
							tabIndex={0}
							width={
								hitAreaBounds(index, points.length, x).right -
								hitAreaBounds(index, points.length, x).left
							}
							x={hitAreaBounds(index, points.length, x).left}
							y={padding.top}
						/>
					</g>
				))}
				{activePoint && activeX !== undefined && (
					<g aria-hidden="true" className="dashboard-chart-active">
						<line
							className="chart-guide"
							x1={activeX}
							x2={activeX}
							y1={padding.top}
							y2={padding.top + plotHeight}
						/>
						<circle
							className="chart-point chart-point-visitors"
							cx={activeX}
							cy={y(activePoint.visitors, visitorsMaximum)}
							r="4"
						/>
						<circle
							className="chart-point chart-point-chats"
							cx={activeX}
							cy={y(activePoint.chats, chatsMaximum)}
							r="4"
						/>
						<g
							className="dashboard-chart-tooltip"
							transform={`translate(${tooltipX(activeX, tooltipWidth, padding.left, width - padding.right)}, ${padding.top + 5})`}
						>
							<rect height="49" rx="6" width={tooltipWidth} />
							<text x="10" y="18">
								<tspan className="chart-tooltip-title">
									{formatDate(`${activePoint.date}T12:00:00`)}
								</tspan>
								<tspan x="10" dy="18">
									{activePoint.chats} chats · {activePoint.visitors} visitors
								</tspan>
							</text>
						</g>
					</g>
				)}
			</svg>
		</div>
	);
}

function coverageIntervalLabel(minutes: number): string {
	if (minutes < 60) return `${minutes}-minute intervals`;
	if (minutes < 1_440) return `${minutes / 60}-hour intervals`;
	if (minutes === 1_440) return 'Daily intervals';
	return `${minutes / 1_440}-day intervals`;
}

export function AgentCoverageChart({ points }: { points: DashboardAgentCoveragePoint[] }) {
	const { formatDate, formatDateTime, formatTime } = useDateFormat();
	const [activeIndex, setActiveIndex] = useState<number>();
	const width = 760;
	const height = 270;
	const padding = { bottom: 47, left: 46, right: 20, top: 20 };
	const plotWidth = width - padding.left - padding.right;
	const plotHeight = height - padding.top - padding.bottom;
	const maximum = Math.max(1, ...points.map((point) => point.agents));
	const axisMax = maximum <= 5 ? 5 : Math.ceil(maximum / 5) * 5;
	const x = (index: number) =>
		padding.left +
		(points.length <= 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth);
	const y = (value: number) => padding.top + plotHeight - (value / axisMax) * plotHeight;
	const line = points.map((point, index) => `${x(index)},${y(point.agents)}`).join(' ');
	const labelInterval = Math.max(1, Math.ceil(points.length / 6));
	const activePoint = activeIndex === undefined ? undefined : points[activeIndex];
	const activeX = activeIndex === undefined ? undefined : x(activeIndex);
	const tooltipWidth = 176;

	if (!points.length) return <div className="dashboard-empty-chart">No agent coverage yet.</div>;
	return (
		<div className="dashboard-chart-wrap">
			<svg
				aria-label="Agent coverage over time"
				className="dashboard-chart"
				onMouseLeave={() => setActiveIndex(undefined)}
				role="group"
				viewBox={`0 0 ${width} ${height}`}
			>
				{[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
					const lineY = padding.top + plotHeight - ratio * plotHeight;
					return (
						<g key={ratio}>
							<line
								x1={padding.left}
								x2={width - padding.right}
								y1={lineY}
								y2={lineY}
							/>
							<text
								className="chart-axis-coverage"
								x={padding.left - 9}
								y={lineY + 4}
							>
								{Math.round(axisMax * ratio)}
							</text>
						</g>
					);
				})}
				<polyline className="chart-line chart-line-coverage" pathLength="1" points={line} />
				{points.map((point, index) => (
					<g key={point.at}>
						{(index % labelInterval === 0 || index === points.length - 1) && (
							<text
								className="chart-date chart-date-time"
								x={x(index)}
								y={height - 20}
							>
								<tspan x={x(index)}>{formatDate(point.at)}</tspan>
								<tspan x={x(index)} dy="12">
									{formatTime(point.at)}
								</tspan>
							</text>
						)}
						<rect
							aria-label={`${formatDateTime(point.at)}: ${point.agents} agents online`}
							className="chart-hit"
							height={plotHeight}
							onBlur={() => setActiveIndex(undefined)}
							onFocus={() => setActiveIndex(index)}
							onMouseEnter={() => setActiveIndex(index)}
							role="img"
							tabIndex={0}
							width={
								hitAreaBounds(index, points.length, x).right -
								hitAreaBounds(index, points.length, x).left
							}
							x={hitAreaBounds(index, points.length, x).left}
							y={padding.top}
						/>
					</g>
				))}
				{activePoint && activeX !== undefined && (
					<g aria-hidden="true" className="dashboard-chart-active">
						<line
							className="chart-guide"
							x1={activeX}
							x2={activeX}
							y1={padding.top}
							y2={padding.top + plotHeight}
						/>
						<circle
							className="chart-point chart-point-coverage"
							cx={activeX}
							cy={y(activePoint.agents)}
							r="4"
						/>
						<g
							className="dashboard-chart-tooltip"
							transform={`translate(${tooltipX(activeX, tooltipWidth, padding.left, width - padding.right)}, ${padding.top + 5})`}
						>
							<rect height="49" rx="6" width={tooltipWidth} />
							<text x="10" y="18">
								<tspan className="chart-tooltip-title">
									{formatDateTime(activePoint.at)}
								</tspan>
								<tspan x="10" dy="18">
									{activePoint.agents} agents online
								</tspan>
							</text>
						</g>
					</g>
				)}
			</svg>
		</div>
	);
}

function AgentAvatar({ agent }: { agent: DashboardAgentSummary }) {
	if (agent.avatarEnabled) {
		return (
			<MemberAvatar
				dataUrl={agent.avatarKind === 'uploaded' ? agent.avatarDataUrl : undefined}
				displayName={agent.displayName}
				enabled
				preset={agent.avatarPreset}
			/>
		);
	}
	return (
		<span className="dashboard-agent-initial">
			{agent.displayName.slice(0, 1).toUpperCase()}
		</span>
	);
}

export function TenantDashboard({
	membership,
	onNavigate,
}: {
	membership: MembershipSummary;
	onNavigate: (destination: DashboardDestination) => void;
}) {
	const [dateRange, setDateRange] = useState(() => recentDateRange());
	const [summary, setSummary] = useState<DashboardSummary>();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string>();
	const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC', []);
	const refreshRunner = useRef(createRefreshRunner()).current;

	const load = useCallback(
		() =>
			refreshRunner.run(async () => {
				setError(undefined);
				try {
					setSummary(
						await getDashboard(membership.tenantId, {
							from: dateRange.from,
							timezone,
							to: dateRange.to,
						}),
					);
				} catch (caught) {
					setError(
						caught instanceof Error ? caught.message : 'Could not load the dashboard',
					);
				} finally {
					setLoading(false);
				}
			}),
		[dateRange.from, dateRange.to, membership.tenantId, refreshRunner, timezone],
	);

	useEffect(() => {
		setLoading(true);
		void load();
	}, [load]);

	useEffect(() => {
		const token = currentAccessToken();
		if (!token) return;
		const socket = io(`${realtimeUrl}/chat`, {
			auth: { accessToken: token, tenantId: membership.tenantId },
			transports: ['websocket', 'polling'],
		});
		let refreshTimer: number | undefined;
		let lastRealtimeRefreshAt = 0;
		const refresh = () => {
			window.clearTimeout(refreshTimer);
			refreshTimer = window.setTimeout(() => void load(), 200);
		};
		const stopWatchingToken = onAccessTokenChanged((updatedToken) => {
			socket.auth = { accessToken: updatedToken, tenantId: membership.tenantId };
			if (updatedToken && !socket.connected) socket.connect();
		});
		for (const event of [
			'conversation:created',
			'conversation:updated',
			'message:created',
			'visitor:presence',
			'visitor:updated',
		]) {
			socket.on(event, refresh);
		}
		socket.on('session:error', () => {
			if (Date.now() - lastRealtimeRefreshAt < 60_000) return;
			lastRealtimeRefreshAt = Date.now();
			void refreshSession()
				.then((authentication) => {
					socket.auth = {
						accessToken: authentication.accessToken,
						tenantId: membership.tenantId,
					};
					socket.connect();
				})
				.catch(() => setError('The realtime dashboard connection could not be restored'));
		});
		const interval = window.setInterval(() => void load(), 30_000);
		return () => {
			window.clearInterval(interval);
			window.clearTimeout(refreshTimer);
			stopWatchingToken();
			socket.disconnect();
		};
	}, [load, membership.tenantId]);

	const metrics: DashboardMetric[] = summary
		? [
				{
					action: 'chats' as const,
					kind: 'chat',
					label: 'Ongoing Chats',
					tone: 'green',
					value: String(summary.overview.ongoingChats),
				},
				{
					action: 'chats' as const,
					kind: 'queue',
					label: 'Waiting Chats',
					tone: 'blue',
					value: String(summary.overview.waitingChats),
				},
				{
					action: 'visitors' as const,
					kind: 'visitors',
					label: 'Visitors Online',
					tone: 'cyan',
					value: String(summary.overview.visitorsOnline),
				},
				{
					action: 'history' as const,
					kind: 'total',
					label: 'Chats Today',
					tone: 'slate',
					value: String(summary.overview.chatsToday),
				},
				{
					action: 'history' as const,
					kind: 'unanswered',
					label: 'Unanswered Today',
					tone: 'amber',
					value: String(summary.overview.unansweredToday),
				},
				{
					kind: 'clock',
					label: 'Avg. First Response',
					tone: 'orange',
					value: formatDuration(summary.overview.averageFirstResponseSeconds),
				},
			]
		: [];
	return (
		<section className="tenant-dashboard" aria-labelledby="dashboard-heading">
			<header className="dashboard-heading">
				<div>
					<h2 id="dashboard-heading">Dashboard</h2>
					<p>Live activity and service performance for {membership.tenantName}.</p>
				</div>
				<div className="dashboard-date-range">
					<DateRangePicker
						includeTime={false}
						onChange={setDateRange}
						value={dateRange}
					/>
				</div>
			</header>

			{error && <div className="alert error dashboard-alert">{error}</div>}
			{loading && !summary ? (
				<div className="dashboard-loading">Loading dashboard…</div>
			) : summary ? (
				<div className="dashboard-grid" aria-busy={loading}>
					<section className="dashboard-panel dashboard-overview-panel">
						<header>
							<h3>Today’s Overview</h3>
							<span>Live</span>
						</header>
						<div className="dashboard-metrics">
							{metrics.map((metric) => (
								<button
									className="dashboard-metric"
									disabled={!metric.action}
									key={metric.label}
									onClick={() => metric.action && onNavigate(metric.action)}
								>
									<span className={`dashboard-metric-icon metric-${metric.tone}`}>
										<MetricIcon kind={metric.kind} />
									</span>
									<span>
										<strong>{metric.value}</strong>
										<small>{metric.label}</small>
									</span>
								</button>
							))}
						</div>
					</section>

					<section className="dashboard-panel dashboard-agents-panel">
						<header>
							<div>
								<h3>Connected Agents</h3>
								<span>{summary.agents.length} online now</span>
							</div>
							{membership.role !== 'agent' && (
								<button className="text-button" onClick={() => onNavigate('team')}>
									View team
								</button>
							)}
						</header>
						<div className="dashboard-agent-list">
							{summary.agents.map((agent) => (
								<div className="dashboard-agent" key={agent.id}>
									<AgentAvatar agent={agent} />
									<div className="dashboard-agent-name">
										<strong>{agent.displayName}</strong>
										<span className="capitalise">
											{teamMemberRoleLabel(agent.memberType, agent.role)}
										</span>
									</div>
									<span
										className={`status-pill presence-${agent.presenceStatus}`}
									>
										{agent.presenceStatus}
									</span>
									<span className="dashboard-agent-capacity">
										<strong>
											{agent.activeChatCount} / {agent.maxSimultaneousChats}
										</strong>
										<small>active chats</small>
									</span>
								</div>
							))}
							{!summary.agents.length && (
								<div className="dashboard-empty">
									No agents are connected at the moment.
								</div>
							)}
						</div>
					</section>

					<section className="dashboard-panel dashboard-trend-panel">
						<header>
							<div>
								<h3>Daily Chats &amp; Visitors</h3>
								<span>{summary.days}-day activity</span>
							</div>
							<div className="dashboard-legend">
								<span className="legend-chats">Daily chats</span>
								<span className="legend-visitors">Daily visitors</span>
							</div>
						</header>
						<TrendChart points={summary.trend} />
					</section>

					<section className="dashboard-panel dashboard-coverage-panel">
						<header>
							<div>
								<h3>Agent Coverage</h3>
								<span>
									Peak agents online ·{' '}
									{coverageIntervalLabel(
										summary.agentCoverageIntervalMinutes ?? 360,
									)}
								</span>
							</div>
							<div className="dashboard-legend">
								<span className="legend-coverage">Agents online</span>
							</div>
						</header>
						<AgentCoverageChart points={summary.agentCoverage ?? []} />
					</section>

					<section className="dashboard-panel dashboard-locations-panel">
						<header>
							<div>
								<h3>Visitor Locations</h3>
								<span>Unique visitors · {summary.days} days</span>
							</div>
						</header>
						<WorldVisitorMap countries={summary.countries} />
					</section>
				</div>
			) : null}
		</section>
	);
}
